import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, catchError, map, of, switchMap } from 'rxjs';

import {
  AUDIENCE_VALUE,
  AssistanceAudience,
  AssistanceModule,
  AssistanceResource,
  CreateAssistanceResourceDto,
  UpdateAssistanceResourceDto,
  UpsertAssistanceModuleDto,
} from '../../../core/interfaces/assistance-resource';
import { AdminAssistanceService } from '../../../core/services/admin-assistance.service';
import { UploadPurpose, UploadResult, UploadService } from '../../../core/services/uploads/upload.service';
import {
  AUDIENCES,
  audienceClass,
  audienceIcon,
  audienceLabelKey,
  formatSize,
  kindIcon,
  summarize,
} from '../../../core/utils/assistance-media';
import { ErrorStateComponent } from '../../../components/ui/error-state/error-state.component';
import { AssistancePlayerComponent } from '../../../components/features/shared/assistance/assistance-player/assistance-player.component';
import { matchesSearch } from '../../../core/utils/search-text';

type DeleteTarget =
  | { kind: 'resource'; item: AssistanceResource }
  | { kind: 'module'; item: AssistanceModule };

interface ModuleView {
  module: AssistanceModule;
  /** Les ressources du module qui répondent à la recherche. */
  shown: AssistanceResource[];
}

/**
 * Gestion de l'aide : un onglet par public, et dans chacun des modules qui
 * portent une série ordonnée.
 *
 * <p>Les positions sont tenues par le serveur, denses dans chaque module et
 * parmi les modules d'un public. Après une écriture qui ajoute, déplace ou
 * archive, la page recharge plutôt que de recalculer ce que le serveur a
 * renuméroté. Seul le réordonnancement d'une série se prépare à l'écran — les
 * flèches s'accumulent et l'ordre complet part en un seul appel.</p>
 */
@Component({
  selector: 'app-admin-assistance',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ErrorStateComponent, AssistancePlayerComponent],
  templateUrl: './admin-assistance.component.html',
  styleUrl: './admin-assistance.component.css',
})
export class AdminAssistanceComponent implements OnInit {
  private readonly service = inject(AdminAssistanceService);
  private readonly uploadService = inject(UploadService);
  private readonly router = inject(Router);

  readonly audiences = AUDIENCES;
  readonly audienceIcon = audienceIcon;
  readonly audienceLabel = audienceLabelKey;
  readonly audienceClass = audienceClass;
  readonly kindIcon = kindIcon;
  readonly formatSize = formatSize;
  readonly summarize = summarize;

  readonly modules = signal<AssistanceModule[]>([]);
  readonly loading = signal(false);
  readonly errored = signal(false);
  /** Clé de traduction du dernier échec d'écriture, affiché jusqu'à fermeture. */
  readonly actionError = signal<string | null>(null);

  readonly tab = signal<AssistanceAudience>('Admin');
  readonly query = signal('');
  readonly openIds = signal<ReadonlySet<number>>(new Set());

  readonly visibleModules = computed<ModuleView[]>(() => {
    const q = this.query();
    return this.modulesOf(this.tab())
      .map((module) => ({
        module,
        shown: matchesSearch(q, module.title, module.description)
          ? module.resources
          : module.resources.filter((r) => matchesSearch(q, r.title, r.description)),
      }))
      .filter((view) => !q.trim() || view.shown.length > 0);
  });

  private readonly destroyRef = inject(DestroyRef);

  /** Chaque demande de chargement ; switchMap ne laisse aboutir que la dernière. */
  private readonly reloads = new Subject<void>();

  ngOnInit(): void {
    this.reloads
      .pipe(
        switchMap(() =>
          this.service.getModules().pipe(
            map((res) => ({ ok: true, data: res?.data ?? [] })),
            catchError(() => of({ ok: false, data: [] as AssistanceModule[] })),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result.ok) {
          this.applyModules(result.data);
          this.ensureOneOpen();
        } else if (this.modules().length) {
          // Rechargement après une écriture : les données affichées restent.
          this.actionError.set('ASSISTANCE.LOAD_ERROR');
        } else {
          this.errored.set(true);
        }
        this.loading.set(false);
      });

    this.load();
  }

  reload(): void {
    this.load();
  }

  /** @param silent recharge après une écriture, sans repasser par le squelette. */
  private load(silent = false): void {
    if (!silent) this.loading.set(true);
    this.errored.set(false);
    this.reloads.next();
  }

  /**
   * Remplace les modules par ceux du serveur sans défaire un ordre en cours.
   *
   * <p>Un rechargement part après une écriture ; l'admin peut avoir déplacé une
   * ressource avant la réponse. Un module encore en attente garde l'ordre
   * affiché, et la référence d'« Annuler » devient l'ordre que le serveur
   * vient de rendre.</p>
   */
  private applyModules(incoming: AssistanceModule[]): void {
    const displayed = new Map(this.modules().map((m) => [m.id, m] as const));
    const present = new Set(incoming.map((m) => m.id));
    const pending = new Set([...this.dirtyIds()].filter((id) => present.has(id)));

    for (const id of [...this.savedOrders.keys()]) {
      if (!pending.has(id)) this.savedOrders.delete(id);
    }

    this.modules.set(
      incoming.map((module) => {
        if (!pending.has(module.id)) return module;
        const rank = new Map((displayed.get(module.id)?.resources ?? []).map((r, i) => [r.id, i] as const));
        const last = Number.MAX_SAFE_INTEGER;
        this.savedOrders.set(module.id, module.resources.map((r) => r.id));
        const ordered = [...module.resources].sort((a, b) => (rank.get(a.id) ?? last) - (rank.get(b.id) ?? last));
        return { ...module, resources: ordered.map((r, i) => ({ ...r, displayOrder: i + 1 })) };
      }),
    );
    this.dirtyIds.set(pending);
  }

  // ── Onglets et modules ────────────────────────────────────

  modulesOf(audience: AssistanceAudience): AssistanceModule[] {
    return this.modules()
      .filter((m) => m.audience === audience)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  setTab(audience: AssistanceAudience): void {
    this.tab.set(audience);
    this.ensureOneOpen();
  }

  isOpen(module: AssistanceModule): boolean {
    return !!this.query().trim() || this.openIds().has(module.id);
  }

  toggle(module: AssistanceModule): void {
    const next = new Set(this.openIds());
    if (next.has(module.id)) next.delete(module.id);
    else next.add(module.id);
    this.openIds.set(next);
  }

  private open(moduleId: number): void {
    this.openIds.set(new Set([...this.openIds(), moduleId]));
  }

  /** Un onglet s'ouvre sur son premier module déplié, pour montrer qu'il porte une série. */
  private ensureOneOpen(): void {
    const tabModules = this.modulesOf(this.tab());
    if (tabModules.length && !tabModules.some((m) => this.openIds().has(m.id))) this.open(tabModules[0].id);
  }

  moduleById(id: number): AssistanceModule | undefined {
    return this.modules().find((m) => m.id === id);
  }

  // ── Réordonnancement ──────────────────────────────────────

  /** Modules dont l'ordre a changé à l'écran sans être enregistré. */
  readonly dirtyIds = signal<ReadonlySet<number>>(new Set());

  /**
   * Un ordre en attente bloque les autres écritures. Elles rechargent la page,
   * ce qui jetterait l'ordre non enregistré — dans ce module et dans les autres —
   * et une modification enverrait la position affichée, pas celle du serveur.
   */
  readonly hasPendingOrder = computed(() => this.dirtyIds().size > 0);
  readonly savingOrder = signal(false);
  private readonly savedOrders = new Map<number, number[]>();

  isDirty(module: AssistanceModule): boolean {
    return this.dirtyIds().has(module.id);
  }

  move(module: AssistanceModule, resource: AssistanceResource, delta: number): void {
    // Pendant l'enregistrement, un déplacement serait écrasé par la réponse.
    if (this.savingOrder()) return;
    const list = [...module.resources];
    const from = list.findIndex((r) => r.id === resource.id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= list.length) return;

    if (!this.savedOrders.has(module.id)) this.savedOrders.set(module.id, list.map((r) => r.id));
    [list[from], list[to]] = [list[to], list[from]];
    this.replaceResources(module.id, list);
    this.dirtyIds.set(new Set([...this.dirtyIds(), module.id]));
  }

  cancelOrder(module: AssistanceModule): void {
    const ids = this.savedOrders.get(module.id);
    if (!ids) return;
    const byId = new Map(module.resources.map((r) => [r.id, r] as const));
    this.replaceResources(module.id, ids.map((id) => byId.get(id)).filter((r): r is AssistanceResource => !!r));
    this.clearDirty(module.id);
  }

  saveOrder(module: AssistanceModule): void {
    this.savingOrder.set(true);
    this.service.reorderModule(module.id, module.resources.map((r) => r.id)).subscribe({
      next: (res) => {
        if (res?.data) this.modules.update((all) => all.map((m) => (m.id === res.data.id ? res.data : m)));
        this.clearDirty(module.id);
        this.savingOrder.set(false);
      },
      error: () => {
        this.savingOrder.set(false);
        this.actionError.set('ASSISTANCE.SAVE_ERROR');
      },
    });
  }

  private replaceResources(moduleId: number, list: AssistanceResource[]): void {
    this.modules.update((all) =>
      all.map((m) =>
        m.id === moduleId ? { ...m, resources: list.map((r, i) => ({ ...r, displayOrder: i + 1 })) } : m,
      ),
    );
  }

  /** Vrai — et le message s'affiche — tant qu'un ordre attend d'être enregistré ou annulé. */
  private blockedByPendingOrder(): boolean {
    if (!this.hasPendingOrder()) return false;
    this.actionError.set('ASSISTANCE.ORDER_PENDING');
    return true;
  }

  private clearDirty(moduleId: number): void {
    this.savedOrders.delete(moduleId);
    const next = new Set(this.dirtyIds());
    next.delete(moduleId);
    this.dirtyIds.set(next);
  }

  // ── Formulaire de module ──────────────────────────────────

  readonly moduleFormOpen = signal(false);
  readonly saving = signal(false);
  editModule: AssistanceModule | null = null;
  moduleForm = { title: '', description: '', audience: 'Admin' as AssistanceAudience, displayOrder: 1 };

  openCreateModule(): void {
    if (this.blockedByPendingOrder()) return;
    this.editModule = null;
    this.moduleForm = {
      title: '',
      description: '',
      audience: this.tab(),
      displayOrder: this.modulesOf(this.tab()).length + 1,
    };
    this.moduleFormOpen.set(true);
  }

  openEditModule(module: AssistanceModule): void {
    if (this.blockedByPendingOrder()) return;
    this.editModule = module;
    this.moduleForm = {
      title: module.title,
      description: module.description ?? '',
      audience: module.audience,
      displayOrder: module.displayOrder,
    };
    this.moduleFormOpen.set(true);
  }

  closeModuleForm(): void {
    this.moduleFormOpen.set(false);
    this.editModule = null;
  }

  submitModule(): void {
    const title = this.moduleForm.title.trim();
    if (!title) return;

    const audience = this.moduleForm.audience;
    const dto: UpsertAssistanceModuleDto = {
      title,
      description: this.moduleForm.description.trim() || undefined,
      audience: AUDIENCE_VALUE[audience],
      displayOrder: Number(this.moduleForm.displayOrder) || 0,
    };
    const request = this.editModule
      ? this.service.updateModule(this.editModule.id, dto)
      : this.service.createModule(dto);

    this.saving.set(true);
    request.subscribe({
      next: (res) => {
        this.saving.set(false);
        this.closeModuleForm();
        this.tab.set(audience);
        if (res?.data) this.open(res.data.id);
        this.load(true);
      },
      error: () => {
        this.saving.set(false);
        this.actionError.set('ASSISTANCE.SAVE_ERROR');
      },
    });
  }

  // ── Formulaire de ressource ───────────────────────────────

  readonly resourceFormOpen = signal(false);
  readonly uploading = signal(false);
  readonly uploadPercent = signal<number | null>(null);
  editResource: AssistanceResource | null = null;
  resourceForm = { title: '', description: '', moduleId: 0, displayOrder: 1, externalUrl: '' };
  selectedFile: File | null = null;

  /** Sans module où ranger la ressource, c'est le module qu'il faut créer d'abord. */
  openCreateResource(module?: AssistanceModule): void {
    if (this.blockedByPendingOrder()) return;
    // Jamais le module d'un autre public : sans module dans l'onglet courant,
    // c'est le module qu'il faut créer — sinon une procédure interne partirait
    // chez les professeurs, présélectionnés en tête de la liste du serveur.
    const target = module ?? this.modulesOf(this.tab())[0];
    if (!target) {
      this.openCreateModule();
      return;
    }
    this.editResource = null;
    this.resourceForm = {
      title: '',
      description: '',
      moduleId: target.id,
      displayOrder: target.resources.length + 1,
      externalUrl: '',
    };
    this.selectedFile = null;
    this.uploadPercent.set(null);
    this.resourceFormOpen.set(true);
  }

  openEditResource(resource: AssistanceResource): void {
    if (this.blockedByPendingOrder()) return;
    this.editResource = resource;
    this.resourceForm = {
      title: resource.title,
      description: resource.description ?? '',
      moduleId: resource.moduleId,
      displayOrder: resource.displayOrder,
      externalUrl: resource.externalUrl ?? '',
    };
    this.selectedFile = null;
    this.resourceFormOpen.set(true);
  }

  closeResourceForm(): void {
    this.resourceFormOpen.set(false);
    this.editResource = null;
  }

  /**
   * Changer de module range la ressource en fin de sa nouvelle série ; revenue
   * dans son module d'origine, elle retrouve sa place. Garder la position
   * d'avant la placerait en tête d'une série qu'elle ne faisait que rejoindre.
   */
  onResourceModuleChange(moduleId: number): void {
    const module = this.moduleById(moduleId);
    if (!module) return;
    const edited = this.editResource;
    this.resourceForm.displayOrder = edited && edited.moduleId === moduleId
      ? edited.displayOrder
      : module.resources.length + 1;
  }

  /** Même règle pour un module qui change de public. */
  onModuleAudienceChange(audience: AssistanceAudience): void {
    const edited = this.editModule;
    this.moduleForm.displayOrder = edited && edited.audience === audience
      ? edited.displayOrder
      : this.modulesOf(audience).length + 1;
  }

  resourceModule(): AssistanceModule | undefined {
    return this.moduleById(this.resourceForm.moduleId);
  }

  canSubmitResource(): boolean {
    if (!this.resourceForm.title.trim() || !this.resourceForm.moduleId) return false;
    return !!this.editResource || !!this.selectedFile || !!this.resourceForm.externalUrl.trim();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.length ? input.files[0] : null;
  }

  submitResource(): void {
    if (!this.canSubmitResource()) return;
    if (this.editResource) this.submitUpdate(this.editResource);
    else this.submitCreate();
  }

  private submitCreate(): void {
    if (!this.selectedFile) {
      this.create(null);
      return;
    }

    this.uploading.set(true);
    this.uploadService.uploadWithProgress(this.selectedFile, UploadPurpose.Assistance).subscribe({
      next: (progress) => {
        this.uploadPercent.set(progress.percent);
        if (progress.done && progress.result) {
          this.uploading.set(false);
          this.create(progress.result);
        }
      },
      error: () => {
        this.uploading.set(false);
        this.actionError.set('ASSISTANCE.SAVE_ERROR');
      },
    });
  }

  private create(upload: UploadResult | null): void {
    const dto: CreateAssistanceResourceDto = {
      title: this.resourceForm.title.trim(),
      description: this.resourceForm.description.trim() || undefined,
      moduleId: this.resourceForm.moduleId,
      displayOrder: Number(this.resourceForm.displayOrder) || 0,
    };
    if (upload) {
      dto.objectKey = upload.key;
      dto.fileName = upload.originalFileName;
      dto.contentType = upload.contentType;
      dto.sizeBytes = upload.sizeBytes;
    } else {
      dto.externalUrl = this.resourceForm.externalUrl.trim();
    }
    this.persist(this.service.create(dto), dto.moduleId);
  }

  private submitUpdate(resource: AssistanceResource): void {
    const dto: UpdateAssistanceResourceDto = {
      title: this.resourceForm.title.trim(),
      description: this.resourceForm.description.trim() || undefined,
      moduleId: this.resourceForm.moduleId,
      displayOrder: Number(this.resourceForm.displayOrder) || 0,
    };
    this.persist(this.service.update(resource.id, dto), dto.moduleId);
  }

  private persist(request: Observable<unknown>, moduleId: number): void {
    this.saving.set(true);
    request.subscribe({
      next: () => {
        this.saving.set(false);
        this.closeResourceForm();
        const module = this.moduleById(moduleId);
        if (module) this.tab.set(module.audience);
        this.open(moduleId);
        this.load(true);
      },
      error: () => {
        this.saving.set(false);
        this.actionError.set('ASSISTANCE.SAVE_ERROR');
      },
    });
  }

  // ── Archivage ─────────────────────────────────────────────

  readonly deleteTarget = signal<DeleteTarget | null>(null);
  readonly deleting = signal(false);

  askArchiveResource(resource: AssistanceResource): void {
    if (this.blockedByPendingOrder()) return;
    this.deleteTarget.set({ kind: 'resource', item: resource });
  }

  askArchiveModule(module: AssistanceModule): void {
    if (this.blockedByPendingOrder()) return;
    this.deleteTarget.set({ kind: 'module', item: module });
  }

  deleteMessage(): { key: string; params: Record<string, unknown> } | null {
    const target = this.deleteTarget();
    if (!target) return null;
    return target.kind === 'module'
      ? { key: 'ASSISTANCE.CONFIRM_ARCHIVE_MODULE', params: { title: target.item.title, count: target.item.resources.length } }
      : { key: 'ASSISTANCE.CONFIRM_DELETE', params: { title: target.item.title } };
  }

  confirmDelete(): void {
    const target = this.deleteTarget();
    if (!target) return;

    const request = target.kind === 'module'
      ? this.service.archiveModule(target.item.id)
      : this.service.archive(target.item.id);

    this.deleting.set(true);
    request.subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteTarget.set(null);
        this.load(true);
      },
      error: () => {
        this.deleting.set(false);
        this.actionError.set('ASSISTANCE.SAVE_ERROR');
      },
    });
  }

  // ── Aperçu, téléchargement, vue utilisateur ───────────────

  readonly playerResource = signal<AssistanceResource | null>(null);
  readonly playerModule = signal<AssistanceModule | null>(null);

  openPlayer(resource: AssistanceResource, module: AssistanceModule): void {
    this.playerModule.set(module);
    this.playerResource.set(resource);
  }

  closePlayer(): void {
    this.playerResource.set(null);
    this.playerModule.set(null);
  }

  onDownload(resource: AssistanceResource): void {
    this.service.download(resource.id).subscribe({
      next: (res) => {
        const blob = res.body;
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resource.fileName ?? resource.title;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
    });
  }

  /** La page de consultation elle-même, pour le public de l'onglet — pas une copie. */
  previewAsUser(): void {
    if (this.blockedByPendingOrder()) return;
    this.router.navigate(['/admin/help'], { queryParams: { as: this.tab() } });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.playerResource()) return; // le lecteur se ferme lui-même
    this.closeResourceForm();
    this.closeModuleForm();
    this.deleteTarget.set(null);
  }

  trackModule(_: number, view: ModuleView): number {
    return view.module.id;
  }

  trackResource(_: number, resource: AssistanceResource): number {
    return resource.id;
  }
}
